import { graphql } from "gatsby";
import React, { FC, useMemo } from "react";

import { usePageFullPath } from "../../../hooks/use-page-full-path";
import { ImageCard, ImageCardProps } from "../../cards/image-card";
import { TagColors } from "../../forms/elements/tags/types";

export interface CustomerStoriesGridItemProps
  extends Omit<Queries.CustomerStoryCardDataFragment, "standardpage"> {
  labels: Record<string, string | null>;
  pageId?: string;
}

export const CustomerStoriesGridItem: FC<CustomerStoriesGridItemProps> = ({
  labels,
  description,
  image,
  name,
  specialization,
  pageId,
}) => {
  const pageLink = usePageFullPath(pageId);
  const tags = useMemo(() => {
    const tags: ImageCardProps["tags"] = [];
    if (specialization?.machiningType?.material) {
      tags.push({
        color: specialization.machiningType.material as TagColors,
        children: labels?.[specialization.machiningType.material],
      });
    }
    if (specialization?.name) {
      tags.push({
        color: TagColors.Neutral,
        children: specialization.name,
      });
    }
    return tags;
  }, [labels, specialization?.machiningType?.material, specialization?.name]);

  return (
    <a href={pageLink ?? ""}>
      <ImageCard
        variant={specialization?.machiningType?.material ?? "primary"}
        title={name ?? null}
        __typename="ContentfulImageCard"
        description={description ?? null}
        image={image ?? null}
        logo={null}
        ctas={null}
        textAlign="center"
        tags={tags}
        keepOriginalAspectRatio={false}
        className="w-full aspect-square lg:max-h-[60vh]"
      />
    </a>
  );
};

export const query = graphql`
  fragment CustomerStoryCardData on ContentfulCustomerStory {
    id
    name
    description {
      raw
    }
    image {
      gatsbyImageData
    }
    specialization {
      name
      machiningType {
        material
      }
    }
    updatedAt
    standardpage {
      id
      enrichmentField {
        ... on ContentfulCustomerStory {
          id
        }
      }
    }
  }
`;
